/*
----- Unlike Match.tsx, this is in a separate file that is mostly AI generated code. Beware. ----
*/
import { MATCH_SOURCE_DUPR, MATCH_SOURCE_LEAGUE } from '@/constants/Common'
import { t } from 'i18next'
import { useState } from 'react'
import { ValidateModal } from './ValidateModal'
import { RejectMatchModal } from './RejectMatchModal'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useMatch } from './Match'
import { hasValidatedField, isValidated, MatchStatus } from './match-card-new-utils'
import { PendingActionsUI } from './Match.web'

export function MatchPendingActions() {
  const { status, team1, team2, matchOwner, matchSource, confirmed, rawMatchData: match } = useMatch()

  const [openValidate, setOpenValidate] = useState(false)
  const [openReject, setOpenReject] = useState(false)
  const { user } = useAppSelector(userSelector)
  const [show, setShow] = useState(true)

  if (!show) return null

  // Must be pending; confirmed matches never show actions
  if (status !== MatchStatus.PENDING || confirmed) return null
  if (!user) return null

  const uid = user.id

  // Is the current user one of the players?
  const userOnT1 = uid === team1.player1.id || uid === team1.player2?.id
  const userOnT2 = uid === team2.player1.id || uid === team2.player2?.id
  const isPlayerInThisMatch = userOnT1 || userOnT2

  // Identify user's team & user-as-player (may be undefined if not a player)
  const myTeam = userOnT1 ? team1 : userOnT2 ? team2 : undefined
  const me = myTeam?.player1.id === uid ? myTeam.player1 : myTeam?.player2?.id === uid ? myTeam.player2 : undefined

  // Model capability: does this payload include the validated flag?
  const modelHasValidatedField = hasValidatedField(team1.player1) || hasValidatedField(team2.player1)

  // Defaults
  let showDelete = false
  let showValidate = false
  let showReject = false

  // Creator can always delete (even if they're not in the players array)
  showDelete = uid === matchOwner && matchSource === MATCH_SOURCE_DUPR

  // Validate/Reject actions only make sense if the user is one of the players
  if (isPlayerInThisMatch) {
    if (matchSource === MATCH_SOURCE_DUPR) {
      if (modelHasValidatedField) {
        // New model: non-creator who hasn't validated yet → Validate/Reject
        if (uid !== matchOwner && !isValidated(me)) {
          showValidate = true
          showReject = true
        }
      } else {
        // Legacy: opposite team of creator can Validate/Reject
        const ownerOnTeam1 = matchOwner === team1.player1.id || matchOwner === team1.player2?.id
        const userOnOppositeTeam = ownerOnTeam1 ? userOnT2 : userOnT1
        if (userOnOppositeTeam) {
          showValidate = true
          showReject = true
        }
      }
    } else if (matchSource === MATCH_SOURCE_LEAGUE) {
      if (modelHasValidatedField) {
        // New model: non-creator & not already validated → Validate (no Reject on league)
        if (uid !== matchOwner && !isValidated(me)) {
          showValidate = true
        }
      } else {
        // Legacy: if creator is NOT on my team → Validate (no Reject on league)
        const creatorOnMyTeam = myTeam?.player1?.id === matchOwner || myTeam?.player2?.id === matchOwner
        if (!creatorOnMyTeam) {
          showValidate = true
        }
      }
    }
  }

  if (!showDelete && !showValidate && !showReject) return null

  return (
    <>
      <PendingActionsUI
        showDelete={showDelete}
        showValidate={showValidate}
        showReject={showReject}
        labels={{
          delete: t('common.delete'),
          validate: t('performance.validate'),
          reject: t('performance.reject'),
        }}
        onDelete={() => setOpenReject(true)}
        onValidate={() => setOpenValidate(true)}
        onReject={() => setOpenReject(true)}
      />

      {/* hierarchy owns modal state; visuals stay in modal components */}
      <ValidateModal
        match={match}
        open={openValidate}
        onClose={() => setOpenValidate(false)}
        onUpdated={() => {
          setShow(false)
        }}
      />
      <RejectMatchModal match={match} open={openReject} onClose={() => setOpenReject(false)} />
    </>
  )
}
